import {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  GripVertical,
  Search,
  Plus,
  Pencil,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  Columns3,
  SlidersHorizontal,
  Download,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { FilterPill } from "@/pages/dashboard/editor/components/filter-pill"
import { FilterForm } from "@/pages/dashboard/editor/components/filter-form"
import {
  ColumnFilterValue,
  FilterTypeId,
  FILTER_TYPES,
  NumberFormatConfig,
  DEFAULT_NUMBER_FORMAT,
  PAGE_SIZES,
  STYLE_OPTIONS,
  SEPARATOR_OPTIONS,
  NumberStyleId,
  SeparatorStyleId,
} from "@/types/editor"
import { formatNumber, customFilterFn, customGlobalFilterFn } from "@/lib/utils/editor"
import { exportTableToCsv, exportTableToExcel } from "@/lib/utils/table-export"

interface TableWidgetProps {
  data?: any
  config?: Record<string, any>
}

export function TableWidget({ data }: TableWidgetProps) {
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([])
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [columnNames, setColumnNames] = useState<Record<string, string>>({})
  const [addFilterOpen, setAddFilterOpen] = useState(false)
  const [addFilterCol, setAddFilterCol] = useState("")
  const [addFilterType, setAddFilterType] = useState<FilterTypeId>("contains")
  const [addFilterValue, setAddFilterValue] = useState("")
  const [addColumnOpen, setAddColumnOpen] = useState(false)
  const [renameCol, setRenameCol] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
  const [columnDrag, setColumnDrag] = useState<{ fromId: string; overId: string | null } | null>(null)
  const [columnFormats, setColumnFormats] = useState<Record<string, Partial<NumberFormatConfig>>>({})

  const transparentDragImage = useMemo(() => {
    const img = new Image()
    img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
    return img
  }, [])

  const handleHeaderDragStart = useCallback(
    (e: React.DragEvent, columnId: string) => {
      e.dataTransfer.setData("text/plain", columnId)
      e.dataTransfer.effectAllowed = "move"
      e.dataTransfer.setDragImage(transparentDragImage, 0, 0)
      setColumnDrag({ fromId: columnId, overId: null })
      document.body.classList.add("cursor-grabbing")
    },
    [transparentDragImage]
  )

  const handleHeaderDragEnd = useCallback(() => {
    setColumnDrag(null)
    document.body.classList.remove("cursor-grabbing")
  }, [])

  const handleHeaderDrop = useCallback((e: React.DragEvent, toId: string) => {
    e.preventDefault()
    const from = e.dataTransfer.getData("text/plain")
    if (!from || from === toId) return
    setColumnOrder((prev) => {
      const fromIdx = prev.indexOf(from)
      const toIdx = prev.indexOf(toId)
      if (fromIdx === -1 || toIdx === -1) return prev
      const next = [...prev]
      next.splice(fromIdx, 1)
      next.splice(toIdx, 0, from)
      return next
    })
    setColumnDrag(null)
    document.body.classList.remove("cursor-grabbing")
  }, [])

  const onFormatChange = useCallback((columnId: string, partial: Partial<NumberFormatConfig>) => {
    setColumnFormats((prev) => ({
      ...prev,
      [columnId]: { ...(prev[columnId] ?? {}), ...partial },
    }))
  }, [])

  const formatCellValue = useCallback(
    (raw: unknown, columnId: string): string => {
      const cfg = columnFormats[columnId]
      if (cfg != null && Object.keys(cfg).length > 0) {
        const merged = { ...DEFAULT_NUMBER_FORMAT, ...cfg }
        const formatted = formatNumber(raw, merged)
        if (formatted != null) return formatted
      }
      return raw != null && raw !== "" ? String(raw) : "—"
    },
    [columnFormats]
  )

  const tableData = useMemo(() => (data && Array.isArray(data) ? data : []), [data])
  const dataKeysKey = tableData.length > 0 ? Object.keys(tableData[0] as object).join(",") : ""

  const columnIsNumeric = useMemo(() => {
    if (tableData.length === 0) return {} as Record<string, boolean>
    const keys = Object.keys(tableData[0] as object)
    const out: Record<string, boolean> = {}
    for (const key of keys) {
      const values = (tableData as Record<string, unknown>[])
        .map((r) => r[key])
        .filter((v) => v != null && (typeof v !== "string" || v !== ""))
      if (values.length === 0) {
        out[key] = false
        continue
      }
      const allNumeric = values.every(
        (v) =>
          (typeof v === "number" && !Number.isNaN(v)) || (typeof v === "string" && v !== "" && !Number.isNaN(Number(v)))
      )
      out[key] = allNumeric
    }
    return out
  }, [tableData])

  const columns = useMemo(() => {
    const arr = tableData
    const keys = arr.length > 0 ? Object.keys(arr[0] as object) : []
    return keys.map(
      (key): ColumnDef<Record<string, unknown>> => ({
        id: key,
        accessorKey: key,
        header: ({ column }) => (
          <HeaderCell
            displayName={columnNames[key] ?? key}
            columnId={key}
            columnFormat={{ ...DEFAULT_NUMBER_FORMAT, ...columnFormats[key] }}
            isNumericColumn={columnIsNumeric[key] ?? false}
            onFormatChange={onFormatChange}
            onRename={() => {
              setRenameCol(key)
              setRenameValue(columnNames[key] ?? key)
            }}
            onHide={() => setColumnVisibility((p) => ({ ...p, [key]: false }))}
            onDragStart={(e) => handleHeaderDragStart(e, column.id)}
            onDragEnd={handleHeaderDragEnd}
          />
        ),
        cell: ({ getValue }) => formatCellValue(getValue(), key),
        size: 150,
        minSize: 100,
        maxSize: 500,
        filterFn: customFilterFn,
      })
    )
  }, [
    tableData,
    columnNames,
    columnFormats,
    columnIsNumeric,
    onFormatChange,
    formatCellValue,
    handleHeaderDragStart,
    handleHeaderDragEnd,
  ])

  // Sync column order and visibility when data columns change
  useEffect(() => {
    if (!dataKeysKey) return
    const keys = dataKeysKey.split(",")
    setColumnOrder((prev) => {
      const combined = [...new Set([...prev.filter((id) => keys.includes(id)), ...keys])]
      return combined.length ? combined : keys
    })
    setColumnVisibility((prev) => {
      const next = { ...prev }
      keys.forEach((k) => {
        if (next[k] === undefined) next[k] = true
      })
      return Object.fromEntries(Object.entries(next).filter(([k]) => keys.includes(k)))
    })
    setColumnFilters((prev) => prev.filter((f) => keys.includes(f.id)))
    setSorting((prev) => prev.filter((s) => keys.includes(s.id)))
    setPagination((p) => ({ ...p, pageIndex: 0 }))
    setColumnFormats((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => keys.includes(k))))
  }, [dataKeysKey])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnOrder,
      columnSizing,
      columnVisibility,
      columnFilters,
      sorting,
      globalFilter,
      pagination,
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: customGlobalFilterFn,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
  })

  const removeFilter = useCallback((id: string) => {
    setColumnFilters((p) => p.filter((f) => f.id !== id))
  }, [])

  const applyRename = useCallback(() => {
    if (renameCol != null && renameValue.trim()) {
      setColumnNames((p) => ({ ...p, [renameCol]: renameValue.trim() }))
    }
    setRenameCol(null)
  }, [renameCol, renameValue])

  const allKeys = useMemo(() => {
    if (tableData.length === 0) return []
    return Object.keys(tableData[0] as object)
  }, [tableData])

  const hiddenColumns = table.getAllLeafColumns().filter((c) => !c.getIsVisible())
  const showColumn = useCallback((columnId: string) => {
    setColumnVisibility((p) => ({ ...p, [columnId]: true }))
    setAddColumnOpen(false)
  }, [])

  if (tableData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-1.5">
      {/* Toolbar: search, filter pills, +Filter, +Column */}
      <div className="flex flex-wrap items-center gap-1.5 pt-2">
        <div className="relative min-w-[140px] max-w-[200px] flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search…"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="h-7 pl-6 pr-2 text-[11px]"
          />
        </div>
        {columnFilters.map((f) => {
          const v = f.value as ColumnFilterValue | undefined
          const label = v?.type ? (FILTER_TYPES.find((x) => x.id === v.type)?.label ?? v.type) : "Filter"
          const dn = columnNames[f.id] ?? f.id
          return (
            <FilterPill
              key={f.id}
              filterId={f.id}
              columnName={dn}
              filterLabel={label}
              filterValue={v}
              allKeys={allKeys}
              columnNames={columnNames}
              onRemove={removeFilter}
              onUpdate={(col, type, val) => {
                const fv: ColumnFilterValue = { type, value: val }
                if (type === "isEmpty" || type === "isNotEmpty") fv.value = undefined
                setColumnFilters((p) => p.map((x) => (x.id === col ? { id: col, value: fv } : x)))
              }}
            />
          )
        })}
        <Popover
          open={addFilterOpen}
          onOpenChange={(open) => {
            setAddFilterOpen(open)
            if (open) {
              setAddFilterCol("")
              setAddFilterType("contains")
              setAddFilterValue("")
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-[10px]">
              <Plus className="h-3 w-3" />
              Filter
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom" className="w-56 p-2">
            <FilterForm
              columnId={addFilterCol}
              filterType={addFilterType}
              filterValue={addFilterValue}
              allKeys={allKeys}
              columnNames={columnNames}
              onColumnChange={setAddFilterCol}
              onFilterTypeChange={setAddFilterType}
              onFilterValueChange={setAddFilterValue}
              onApply={(col, type, val) => {
                if (!col) return
                const fv: ColumnFilterValue = { type, value: val }
                if (type === "isEmpty" || type === "isNotEmpty") fv.value = undefined
                const existing = columnFilters.find((f) => f.id === col)
                if (existing) {
                  setColumnFilters((p) => p.map((x) => (x.id === col ? { id: col, value: fv } : x)))
                } else {
                  setColumnFilters((p) => [...p, { id: col, value: fv }])
                }
                setAddFilterOpen(false)
                setAddFilterCol("")
                setAddFilterType("contains")
                setAddFilterValue("")
              }}
              onCancel={() => setAddFilterOpen(false)}
            />
          </PopoverContent>
        </Popover>
        <Popover open={addColumnOpen} onOpenChange={setAddColumnOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-[10px]">
              <Columns3 className="h-3 w-3" />
              Column
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2">
            <div className="space-y-1">
              <Label className="text-[10px]">Add column from table</Label>
              {hiddenColumns.length === 0 ? (
                <p className="py-2 text-[11px] text-gray-500">All columns are visible.</p>
              ) : (
                <div className="max-h-48 space-y-0.5 overflow-y-auto">
                  {hiddenColumns.map((col) => (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => showColumn(col.id)}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[11px] hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <Plus className="h-3 w-3 shrink-0 text-gray-400" />
                      <span className="min-w-0 truncate">{columnNames[col.id] ?? col.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-[10px]">
              <Download className="h-3 w-3" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-40">
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                const cols = table.getVisibleLeafColumns().map((col) => ({
                  id: col.id,
                  header: columnNames[col.id] ?? col.id,
                }))
                const rows = table.getFilteredRowModel().rows.map((r) => r.original)
                exportTableToCsv(rows, cols, "table.csv")
              }}
            >
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onClick={() => {
                const cols = table.getVisibleLeafColumns().map((col) => ({
                  id: col.id,
                  header: columnNames[col.id] ?? col.id,
                }))
                const rows = table.getFilteredRowModel().rows.map((r) => r.original)
                exportTableToExcel(rows, cols, "table.xlsx")
              }}
            >
              Export as Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Rename dialog */}
      <Dialog open={renameCol != null} onOpenChange={(o) => !o && setRenameCol(null)}>
        <DialogContent className="max-w-xs p-4">
          <DialogHeader>
            <DialogTitle className="text-sm">Rename column</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-[10px]">Name</Label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyRename()}
              className="h-8 text-xs"
            />
          </div>
          <DialogFooter className="gap-1 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setRenameCol(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={applyRename}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto">
        <table
          className="min-w-full border-collapse text-[11px]"
          style={{ tableLayout: "fixed", width: Math.max(table.getTotalSize(), 1) }}
        >
          <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{ width: h.getSize(), minWidth: h.getSize() }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.dataTransfer.dropEffect = "move"
                      setColumnDrag((prev) =>
                        prev && prev.fromId !== h.column.id ? { ...prev, overId: h.column.id } : prev
                      )
                    }}
                    onDrop={(e) => handleHeaderDrop(e, h.column.id)}
                    className={cn(
                      "relative border-b border-gray-200 px-2 py-1.5 font-medium text-gray-700 dark:border-gray-700 dark:text-gray-300",
                      columnFormats[h.column.id]?.align === "right" ? "text-right" : "text-left",
                      columnDrag?.overId === h.column.id &&
                        columnDrag.fromId !== h.column.id &&
                        "ring-2 ring-inset ring-blue-400"
                    )}
                  >
                    <div className="flex items-center gap-0.5">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {h.column.getCanSort() && (
                        <button
                          type="button"
                          onClick={h.column.getToggleSortingHandler()}
                          className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
                        >
                          {h.column.getIsSorted() === "asc" && <ArrowUp className="h-3 w-3" />}
                          {h.column.getIsSorted() === "desc" && <ArrowDown className="h-3 w-3" />}
                          {!h.column.getIsSorted() && <ArrowUpDown className="h-3 w-3" />}
                        </button>
                      )}
                      <div
                        onMouseDown={h.getResizeHandler()}
                        onTouchStart={h.getResizeHandler()}
                        className={cn(
                          "absolute right-0 top-0 z-20 h-full w-2 cursor-col-resize touch-none select-none",
                          "hover:bg-blue-400/40 active:bg-blue-400/60",
                          h.column.getIsResizing() && "bg-blue-400/60"
                        )}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, table.getVisibleLeafColumns().length)}
                  className="py-6 text-center text-[11px] text-gray-500"
                >
                  No rows match filters.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-gray-50/50 dark:border-gray-800 dark:hover:bg-gray-800/30"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{ width: cell.column.getSize() }}
                      className={cn(
                        "truncate px-2 py-1 text-gray-900 dark:text-gray-100",
                        columnFormats[cell.column.id]?.align === "right" && "text-right"
                      )}
                      title={String(cell.getValue() ?? "")}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 px-2 py-1 dark:border-gray-700">
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span>Rows per page</span>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(v) => setPagination((p) => ({ ...p, pageSize: Number(v), pageIndex: 0 }))}
          >
            <SelectTrigger className="h-6 w-[70px] text-[10px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((n) => (
                <SelectItem className="text-xs" key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>
            {pagination.pageIndex * pagination.pageSize + 1}–
            {Math.min((pagination.pageIndex + 1) * pagination.pageSize, table.getFilteredRowModel().rows.length)} of{" "}
            {table.getFilteredRowModel().rows.length}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function HeaderCell({
  displayName,
  columnId,
  columnFormat,
  isNumericColumn,
  onFormatChange,
  onRename,
  onHide,
  onDragStart,
  onDragEnd,
}: {
  displayName: string
  columnId: string
  columnFormat: NumberFormatConfig
  isNumericColumn: boolean
  onFormatChange: (columnId: string, partial: Partial<NumberFormatConfig>) => void
  onRename: () => void
  onHide: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragEnd: () => void
}) {
  const f = columnFormat
  return (
    <div className="group flex min-w-0 flex-1 items-center gap-0.5">
      <span
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="shrink-0 cursor-grab rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 active:cursor-grabbing dark:hover:bg-gray-700 dark:hover:text-gray-400"
      >
        <GripVertical className="h-3 w-3" />
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <span
            className="min-w-0 cursor-pointer truncate rounded px-0.5 py-0.5 hover:underline hover:underline-offset-2"
            title={displayName}
          >
            {displayName}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[10rem]">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-xs">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Format column
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[320px] w-64 overflow-y-auto p-2">
              <div className="space-y-2">
                <Label className="text-[10px] font-medium">{isNumericColumn ? "Number format" : "Column format"}</Label>
                {isNumericColumn && (
                  <>
                    <div>
                      <Label className="text-[10px]">Style</Label>
                      <Select
                        value={f.style}
                        onValueChange={(v) => onFormatChange(columnId, { style: v as NumberStyleId })}
                      >
                        <SelectTrigger className="mt-0.5 h-7 w-full text-[11px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STYLE_OPTIONS.map((o) => (
                            <SelectItem className="text-xs" key={o.id} value={o.id}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px]">Separator style</Label>
                      <Select
                        value={f.separatorStyle}
                        onValueChange={(v) =>
                          onFormatChange(columnId, {
                            separatorStyle: v as SeparatorStyleId,
                          })
                        }
                      >
                        <SelectTrigger className="mt-0.5 h-7 w-full text-[11px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEPARATOR_OPTIONS.map((o) => (
                            <SelectItem className="text-xs" key={o.id} value={o.id}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px]">Number of decimal places</Label>
                      <Input
                        type="number"
                        min={0}
                        value={f.decimalPlaces}
                        onChange={(e) =>
                          onFormatChange(columnId, {
                            decimalPlaces: Math.max(0, parseInt(e.target.value, 10) || 0),
                          })
                        }
                        className="mt-0.5 h-7 text-[11px]"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Multiply by a number</Label>
                      <Input
                        type="number"
                        step="any"
                        value={f.multiplyBy}
                        onChange={(e) =>
                          onFormatChange(columnId, {
                            multiplyBy: parseFloat(e.target.value) || 1,
                          })
                        }
                        className="mt-0.5 h-7 text-[11px]"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Add a prefix</Label>
                      <Input
                        value={f.prefix}
                        onChange={(e) => onFormatChange(columnId, { prefix: e.target.value })}
                        placeholder="e.g. $"
                        className="mt-0.5 h-7 text-[11px]"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px]">Add a suffix</Label>
                      <Input
                        value={f.suffix}
                        onChange={(e) => onFormatChange(columnId, { suffix: e.target.value })}
                        placeholder="e.g. dollars"
                        className="mt-0.5 h-7 text-[11px]"
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-[10px]">Align</Label>
                  <Select
                    value={f.align}
                    onValueChange={(v) =>
                      onFormatChange(columnId, {
                        align: v as "left" | "right",
                      })
                    }
                  >
                    <SelectTrigger className="mt-0.5 h-7 w-full text-[11px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem className="text-xs" value="left">
                        Left
                      </SelectItem>
                      <SelectItem className="text-xs" value="right">
                        Right
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onSelect={onRename} className="text-xs">
            <Pencil className="h-3.5 w-3.5" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onHide} className="text-xs">
            <EyeOff className="h-3.5 w-3.5" />
            Hide
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
